/**
 * Intelligence-layer hooks (FND-010/011, AI-001/002/004, PAC-019).
 *
 * Surfaces the advisory AI/ML layer through the AI Gateway / Portals BFF. Every
 * surface is advisory-only (the human confirms) and degrades gracefully on 404
 * to a deterministic seed so the demo always renders the design:
 *
 *   - usePropensity(caseId)   GET  /ai/propensity?case_id=          (AI-001)
 *       falls back to POST /ai/propensity-score:invoke (the live gateway
 *       capability) before seeding.
 *   - useEligibilityRealtime  GET  /ai/eligibility-realtime?case_id= (PAC-019)
 *   - useQueueRanking(role)   GET  /ai/queue-ranking?role=           (AI-002)
 *   - useModelRegistry        GET  /ai/models                        (FND-011)
 *   - useRevenueProjection    GET  /ai/revenue-projection            (AI-004)
 *
 * Wire envelopes are tolerated defensively (`{ items }` / bare array / object /
 * the gateway's `AIInteraction.output` map).
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';
import type { RoleViewKey } from '../components/roleViews';

// ---------------------------------------------------------------------------
// Propensity scoring (AI-001)
// ---------------------------------------------------------------------------

/** A single weighted factor explaining a propensity score (AI-001). */
export interface PropensityFactor {
  label: string;
  /** Weight 0..1 for the bar viz. */
  weight: number;
}

/**
 * Advisory propensity score for a case (AI-001).
 *
 * Matches the backend `PropensityScore` (services/ai_gateway/schemas.py):
 * `score`, `band` (high|med|low), `top_factors[{name,weight}]`, `model_version`,
 * `champion` (the rules baseline it's compared against), and `explanation`.
 */
export interface PropensityScore {
  score: number;
  band: 'high' | 'med' | 'low';
  factors: PropensityFactor[];
  model_version: string;
  /** The incumbent baseline the v2 challenger is compared against. */
  champion: string;
  explanation: string;
}

function bandForScore(score: number): PropensityScore['band'] {
  if (score >= 0.66) return 'high';
  if (score >= 0.4) return 'med';
  return 'low';
}

function seedPropensity(caseId: string): PropensityScore {
  // Deterministic-ish from the case id so repeat views are stable.
  const h = [...caseId].reduce((a, c) => a + c.charCodeAt(0), 0);
  const score = 0.55 + ((h % 35) / 100);
  return {
    score,
    band: bandForScore(score),
    model_version: 'v2',
    champion: 'rules-v1',
    explanation: 'Challenger model favors this case on balance and plan-match strength vs the rules baseline.',
    factors: [
      { label: 'Account balance', weight: 0.34 },
      { label: 'Plan match strength', weight: 0.27 },
      { label: 'Contact recency', weight: 0.18 },
      { label: 'Household eligibility', weight: 0.12 },
      { label: 'Prior enrollment history', weight: 0.09 },
    ],
  };
}

interface RawPropensity {
  case_id?: string;
  score?: number;
  band?: string;
  top_factors?: { name?: string; weight?: number }[];
  model_version?: string;
  champion?: string;
  explanation?: string;
  // Gateway AIInteraction envelope (when reached via :invoke fallback).
  output?: { priority_score?: number; factors?: (string | { name?: string; label?: string; weight?: number })[] };
}

function normalizeBand(b: string | undefined, score: number): PropensityScore['band'] {
  const v = (b ?? '').toLowerCase();
  if (v === 'high') return 'high';
  if (v === 'med' || v === 'medium') return 'med';
  if (v === 'low') return 'low';
  return bandForScore(score);
}

function adaptPropensity(caseId: string, raw: RawPropensity | null | undefined): PropensityScore {
  if (!raw || typeof raw !== 'object') return seedPropensity(caseId);
  // Native /ai/propensity shape.
  if (raw.score != null) {
    const factors: PropensityFactor[] =
      raw.top_factors && raw.top_factors.length
        ? raw.top_factors.map((f) => ({ label: (f.name ?? '—').replace(/_/g, ' '), weight: f.weight ?? 0.1 }))
        : seedPropensity(caseId).factors;
    return {
      score: raw.score,
      band: normalizeBand(raw.band, raw.score),
      factors,
      model_version: raw.model_version ?? 'v2',
      champion: raw.champion ?? 'rules-v1',
      explanation: raw.explanation ?? '',
    };
  }
  // Gateway :invoke fallback envelope.
  const out = raw.output;
  if (out?.priority_score != null) {
    const rawFactors = out.factors ?? [];
    const factors: PropensityFactor[] =
      rawFactors.length > 0
        ? rawFactors.map((f, i) =>
            typeof f === 'string'
              ? { label: f.replace(/_/g, ' '), weight: Math.max(0.05, 0.35 - i * 0.07) }
              : { label: (f.name ?? f.label ?? '—').replace(/_/g, ' '), weight: f.weight ?? Math.max(0.05, 0.35 - i * 0.07) }
          )
        : seedPropensity(caseId).factors;
    return {
      score: out.priority_score,
      band: bandForScore(out.priority_score),
      factors,
      model_version: 'v2',
      champion: 'rules-v1',
      explanation: '',
    };
  }
  return seedPropensity(caseId);
}

interface UsePropensityResult {
  propensity: PropensityScore | null;
  available: boolean;
  isLoading: boolean;
  refresh: () => void;
}

/** Loads the advisory propensity score for a case (AI-001). */
export function usePropensity(caseId: string | undefined): UsePropensityResult {
  const [propensity, setPropensity] = useState<PropensityScore | null>(null);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(() => {
    if (!caseId) return;
    setIsLoading(true);
    const cid = encodeURIComponent(caseId);
    apiClient
      .get<RawPropensity>(`/ai/propensity?case_id=${cid}`)
      .then((data) => {
        setPropensity(adaptPropensity(caseId, data));
        setAvailable(true);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          // Fall back to the live gateway capability before seeding.
          apiClient
            .post<RawPropensity>('/ai/propensity-score:invoke', { case_id: caseId, input: { case_id: caseId } })
            .then((data) => {
              setPropensity(adaptPropensity(caseId, data));
              setAvailable(true);
            })
            .catch(() => {
              setPropensity(seedPropensity(caseId));
              setAvailable(false);
            });
          return;
        }
        setPropensity(seedPropensity(caseId));
        setAvailable(false);
      })
      .finally(() => setIsLoading(false));
  }, [caseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { propensity, available, isLoading, refresh };
}

// ---------------------------------------------------------------------------
// Real-time eligibility (PAC-019)
// ---------------------------------------------------------------------------

/** Real-time eligibility indicator for a case (PAC-019). */
export interface EligibilityRealtime {
  /** eligible | ineligible | pending | unknown. */
  status: 'eligible' | 'ineligible' | 'pending' | 'unknown';
  source: string;
  checked_at: string;
  plan?: string | null;
  detail?: string | null;
}

function normalizeEligStatus(s: string | undefined): EligibilityRealtime['status'] {
  const v = (s ?? '').toLowerCase();
  if (['eligible', 'active', 'covered'].includes(v)) return 'eligible';
  if (['ineligible', 'inactive', 'not_covered', 'denied'].includes(v)) return 'ineligible';
  if (['pending', 'in_progress', 'running', 'queued'].includes(v)) return 'pending';
  return 'unknown';
}

function seedEligibility(): EligibilityRealtime {
  return {
    status: 'eligible',
    source: 'OneSource (cached)',
    checked_at: new Date(Date.now() - 1000 * 60 * 18).toISOString(),
    plan: 'State Medicaid — Adult Group',
    detail: 'Active coverage confirmed; effective date matches recommendation.',
  };
}

/**
 * Raw `/ai/eligibility-realtime` shape (services/ai_gateway/schemas.py):
 * `{ case_id, result, checked_at, source }`. `result` is the status string.
 */
interface RawEligibility {
  case_id?: string;
  result?: string;
  status?: string;
  source?: string;
  connector?: string;
  checked_at?: string;
  updated_at?: string;
  plan?: string | null;
  detail?: string | null;
}

function adaptEligibility(raw: RawEligibility | null | undefined): EligibilityRealtime {
  if (!raw || typeof raw !== 'object') return seedEligibility();
  const status = raw.result ?? raw.status;
  if (!status) return seedEligibility();
  return {
    status: normalizeEligStatus(status),
    source: raw.source ?? raw.connector ?? 'eligibility',
    checked_at: raw.checked_at ?? raw.updated_at ?? new Date().toISOString(),
    plan: raw.plan ?? null,
    detail: raw.detail ?? null,
  };
}

interface UseEligibilityResult {
  eligibility: EligibilityRealtime | null;
  available: boolean;
  isLoading: boolean;
  refresh: () => void;
}

/** Loads the real-time eligibility indicator for a case (PAC-019). */
export function useEligibilityRealtime(caseId: string | undefined): UseEligibilityResult {
  const [eligibility, setEligibility] = useState<EligibilityRealtime | null>(null);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(() => {
    if (!caseId) return;
    setIsLoading(true);
    apiClient
      .get<RawEligibility>(`/ai/eligibility-realtime?case_id=${encodeURIComponent(caseId)}`)
      .then((data) => {
        setEligibility(adaptEligibility(data));
        setAvailable(true);
      })
      .catch(() => {
        setEligibility(seedEligibility());
        setAvailable(false);
      })
      .finally(() => setIsLoading(false));
  }, [caseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { eligibility, available, isLoading, refresh };
}

// ---------------------------------------------------------------------------
// AI queue-ranking (AI-002)
// ---------------------------------------------------------------------------

/**
 * A model-vs-rules ranking entry for a queued case (AI-002).
 *
 * Matches the backend `QueueRankItem` (services/ai_gateway/schemas.py):
 * `{ case_id, rank, model_score, rules_score }`.
 */
export interface QueueRankEntry {
  case_id: string;
  model_rank: number;
  model_score: number;
  rules_score: number;
}

interface RawQueueRanking {
  ranked?: { case_id?: string; rank?: number; model_score?: number; rules_score?: number }[];
  items?: RawQueueRanking['ranked'];
  rankings?: RawQueueRanking['ranked'];
}

function unwrapRanking(data: RawQueueRanking | RawQueueRanking['ranked'] | null | undefined): QueueRankEntry[] {
  const arr = Array.isArray(data) ? data : (data?.ranked ?? data?.items ?? data?.rankings ?? []);
  return (arr ?? []).map((r, i) => ({
    case_id: r.case_id ?? `case-${i}`,
    model_rank: r.rank ?? i + 1,
    model_score: r.model_score ?? 0,
    rules_score: r.rules_score ?? 0,
  }));
}

interface UseQueueRankingResult {
  /** Map of case_id → ranking entry (only populated when live). */
  byCase: Record<string, QueueRankEntry>;
  available: boolean;
  refresh: () => void;
}

/** Loads the AI queue-ranking overlay for a role (AI-002). */
export function useQueueRanking(role: string | undefined, enabled: boolean): UseQueueRankingResult {
  const [byCase, setByCase] = useState<Record<string, QueueRankEntry>>({});
  const [available, setAvailable] = useState(false);

  const refresh = useCallback(() => {
    if (!enabled) return;
    const q = role ? `?role=${encodeURIComponent(role)}` : '';
    apiClient
      .get<RawQueueRanking>(`/ai/queue-ranking${q}`)
      .then((data) => {
        const entries = unwrapRanking(data);
        const map: Record<string, QueueRankEntry> = {};
        for (const e of entries) map[e.case_id] = e;
        setByCase(map);
        setAvailable(entries.length > 0);
      })
      .catch(() => {
        // No live ranking — the page derives a client-side model rank instead.
        setByCase({});
        setAvailable(false);
      });
  }, [role, enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { byCase, available, refresh };
}

// ---------------------------------------------------------------------------
// Model registry (FND-011)
// ---------------------------------------------------------------------------

/**
 * A row in the AI/ML model registry (FND-011).
 *
 * Matches the backend `ModelRegistryEntry` (services/ai_gateway/schemas.py):
 * `id`, `capability`, `version`, `status` (champion|challenger|shadow|retired),
 * `drift` (none|low|moderate|high — categorical), `last_eval` (ISO date), and
 * `approval_gate` (approved|pending_review|blocked).
 */
export interface ModelRegistryEntry {
  id: string;
  capability: string;
  version: string;
  status: string;
  /** Categorical drift band: none | low | moderate | high. */
  drift: string;
  last_eval: string;
  approval_gate: string;
}

const SEED_MODELS: ModelRegistryEntry[] = [
  { id: 'propensity-v2', capability: 'Propensity scoring', version: 'v2.1.0', status: 'champion', drift: 'low', last_eval: '2026-06-01', approval_gate: 'approved' },
  { id: 'queue-rank-v2', capability: 'Queue ranking', version: 'v2.0.0', status: 'champion', drift: 'low', last_eval: '2026-06-03', approval_gate: 'approved' },
  { id: 'doc-classify-v1', capability: 'Document classification', version: 'v1.0.4', status: 'champion', drift: 'none', last_eval: '2026-05-28', approval_gate: 'approved' },
  { id: 'plan-elig-v1', capability: 'Plan eligibility support', version: 'v0.9.0', status: 'challenger', drift: 'moderate', last_eval: '2026-06-10', approval_gate: 'pending_review' },
  { id: 'revenue-forecast-v2', capability: 'Revenue projection', version: 'v2.0.0', status: 'shadow', drift: 'high', last_eval: '2026-06-12', approval_gate: 'pending_review' },
  { id: 'maya-card-v1', capability: 'Maya assistant', version: 'v1.1.0', status: 'champion', drift: 'low', last_eval: '2026-06-05', approval_gate: 'approved' },
];

interface RawModels {
  models?: Partial<ModelRegistryEntry>[];
  items?: Partial<ModelRegistryEntry>[];
}

function unwrapModels(data: RawModels | Partial<ModelRegistryEntry>[] | null | undefined): ModelRegistryEntry[] {
  const arr = Array.isArray(data) ? data : (data?.models ?? data?.items ?? []);
  if (!arr.length) return [];
  return arr.map((m, i) => ({
    id: m.id ?? `model-${i}`,
    capability: m.capability ?? '—',
    version: m.version ?? 'v1',
    status: m.status ?? 'champion',
    drift: typeof m.drift === 'string' ? m.drift : 'none',
    last_eval: m.last_eval ?? '—',
    approval_gate: m.approval_gate ?? 'approved',
  }));
}

interface UseModelRegistryResult {
  models: ModelRegistryEntry[];
  available: boolean;
  isLoading: boolean;
  refresh: () => void;
}

/** Loads the AI/ML model registry (FND-011), seeding on 404. */
export function useModelRegistry(): UseModelRegistryResult {
  const [models, setModels] = useState<ModelRegistryEntry[]>([]);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(() => {
    setIsLoading(true);
    apiClient
      .get<RawModels>('/ai/models')
      .then((data) => {
        const rows = unwrapModels(data);
        setModels(rows.length ? rows : SEED_MODELS);
        setAvailable(rows.length > 0);
      })
      .catch(() => {
        setModels(SEED_MODELS);
        setAvailable(false);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { models, available, isLoading, refresh };
}

// ---------------------------------------------------------------------------
// Revenue projection (AI-004)
// ---------------------------------------------------------------------------

/** One month of the forecast-vs-actual revenue projection (AI-004). */
export interface ProjectionPoint {
  label: string;
  actual: number | null;
  forecast: number;
}

/**
 * The revenue-projection view (AI-004).
 *
 * Matches the backend `RevenueProjection` (services/ai_gateway/schemas.py):
 * `hospital_forecast: [{month, projected, actual}]`, `portfolio_total`, and a
 * stated `accuracy_band` (e.g. "±10%").
 */
export interface RevenueProjection {
  /** Stated accuracy band, e.g. "±10%". */
  accuracy_band: string;
  points: ProjectionPoint[];
  /** Projected full-period / portfolio total. */
  projected_total: number;
}

const SEED_PROJECTION: RevenueProjection = {
  accuracy_band: '±10%',
  projected_total: 16240000,
  points: [
    { label: '2026-01', actual: 842000, forecast: 820000 },
    { label: '2026-02', actual: 905000, forecast: 910000 },
    { label: '2026-03', actual: 988000, forecast: 975000 },
    { label: '2026-04', actual: 1043000, forecast: 1060000 },
    { label: '2026-05', actual: 1172000, forecast: 1150000 },
    { label: '2026-06', actual: 1284500, forecast: 1270000 },
    { label: '2026-07', actual: null, forecast: 1340000 },
    { label: '2026-08', actual: null, forecast: 1405000 },
    { label: '2026-09', actual: null, forecast: 1470000 },
  ],
};

interface RawProjection {
  accuracy_band?: string;
  portfolio_total?: number;
  projected_total?: number;
  hospital_forecast?: { month?: string; projected?: number; actual?: number | null }[];
  points?: { label?: string; actual?: number | null; forecast?: number }[];
}

function adaptProjection(raw: RawProjection | null | undefined): RevenueProjection {
  if (!raw || typeof raw !== 'object') return SEED_PROJECTION;
  // Native shape: hospital_forecast[{month, projected, actual}].
  if (raw.hospital_forecast && raw.hospital_forecast.length) {
    const points: ProjectionPoint[] = raw.hospital_forecast.map((p) => ({
      label: p.month ?? '',
      actual: p.actual ?? null,
      forecast: p.projected ?? 0,
    }));
    return {
      accuracy_band: raw.accuracy_band ?? '±10%',
      projected_total: raw.portfolio_total ?? raw.projected_total ?? points.reduce((a, p) => a + p.forecast, 0),
      points,
    };
  }
  // Tolerated alt shape: points[{label, actual, forecast}].
  if (raw.points && raw.points.length) {
    const points: ProjectionPoint[] = raw.points.map((p) => ({
      label: p.label ?? '',
      actual: p.actual ?? null,
      forecast: p.forecast ?? 0,
    }));
    return {
      accuracy_band: raw.accuracy_band ?? '±10%',
      projected_total: raw.portfolio_total ?? raw.projected_total ?? points.reduce((a, p) => a + p.forecast, 0),
      points,
    };
  }
  return SEED_PROJECTION;
}

interface UseRevenueProjectionResult {
  projection: RevenueProjection | null;
  available: boolean;
  isLoading: boolean;
  refresh: () => void;
}

/** Loads the revenue projection forecast-vs-actual (AI-004), seeding on 404. */
export function useRevenueProjection(): UseRevenueProjectionResult {
  const [projection, setProjection] = useState<RevenueProjection | null>(null);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(() => {
    setIsLoading(true);
    apiClient
      .get<RawProjection>('/ai/revenue-projection')
      .then((data) => {
        setProjection(adaptProjection(data));
        setAvailable(true);
      })
      .catch(() => {
        setProjection(SEED_PROJECTION);
        setAvailable(false);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { projection, available, isLoading, refresh };
}

// ---------------------------------------------------------------------------
// Next-best-action (advisory / HITL)
// ---------------------------------------------------------------------------

/**
 * A stage-aware next-best-action recommendation for a case (advisory / HITL).
 *
 * Matches the backend `NextBestAction` (services/ai_gateway/schemas.py):
 * `{ case_id, stage, action, rationale, confidence, channel, model_version }`.
 */
export interface NextBestAction {
  case_id: string;
  stage: string;
  action: string;
  rationale: string;
  confidence: number;
  channel?: string | null;
  model_version: string;
}

/** Seed default preserves the intake "no-answer → SMS consent link" guidance. */
function seedNextBestAction(caseId: string): NextBestAction {
  return {
    case_id: caseId,
    stage: 'intake',
    action:
      'Two contact attempts logged with no answer — send an SMS consent link before the next call window.',
    rationale: 'An SMS consent link reduces ghosting risk ahead of the next call attempt.',
    confidence: 0.78,
    channel: 'sms',
    model_version: 'v1',
  };
}

interface RawNextBestAction {
  case_id?: string;
  stage?: string;
  action?: string;
  rationale?: string;
  confidence?: number;
  channel?: string | null;
  model_version?: string;
}

function adaptNextBestAction(caseId: string, raw: RawNextBestAction | null | undefined): NextBestAction {
  if (!raw || typeof raw !== 'object' || !raw.action) return seedNextBestAction(caseId);
  return {
    case_id: raw.case_id ?? caseId,
    stage: raw.stage ?? 'intake',
    action: raw.action,
    rationale: raw.rationale ?? '',
    confidence: raw.confidence ?? 0.78,
    channel: raw.channel ?? null,
    model_version: raw.model_version ?? 'v1',
  };
}

interface UseNextBestActionResult {
  action: NextBestAction | null;
  available: boolean;
  isLoading: boolean;
  refresh: () => void;
}

/** Loads the stage-aware next-best-action for a case (advisory), seeding on 404. */
export function useNextBestAction(caseId: string | undefined): UseNextBestActionResult {
  const [action, setAction] = useState<NextBestAction | null>(null);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(() => {
    if (!caseId) return;
    setIsLoading(true);
    apiClient
      .get<RawNextBestAction>(`/ai/next-best-action?case_id=${encodeURIComponent(caseId)}`)
      .then((data) => {
        setAction(adaptNextBestAction(caseId, data));
        setAvailable(true);
      })
      .catch(() => {
        setAction(seedNextBestAction(caseId));
        setAvailable(false);
      })
      .finally(() => setIsLoading(false));
  }, [caseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { action, available, isLoading, refresh };
}

// ---------------------------------------------------------------------------
// Platform impact / scale (advisory) — role-scoped "the platform is scaling
// your work" view. Names the transformation's value in the discovery-deck's own
// numbers per role. See services/ai_gateway GET /ai/platform-impact.
// ---------------------------------------------------------------------------

/** One headline productivity/scale statistic for the Platform Impact strip. */
export interface ImpactMetric {
  key: string;
  label: string;
  value: number;
  unit: string;
  /** Enable 1.0 comparator, when one exists. */
  baseline?: number | null;
  /** Which way is "good" — drives delta colorization. */
  direction: 'up' | 'down';
}

/** How today's workflow steps split across automation vs AI-assist vs manual. */
export interface WorkMix {
  automated_pct: number;
  ai_assisted_pct: number;
  manual_pct: number;
}

/** The role-scoped platform-impact / scale advisory view. */
export interface PlatformImpact {
  role: string;
  headline: string;
  subhead: string;
  metrics: ImpactMetric[];
  work_mix: WorkMix;
  steps_before: number;
  steps_after: number;
  capabilities: string[];
  confidence: number;
}

const IMPACT_WORK_MIX: WorkMix = { automated_pct: 41, ai_assisted_pct: 45, manual_pct: 14 };

/** Static seed metric catalogue (deck-grounded) used when the endpoint is absent. */
const IMPACT_METRIC_SEED: Record<string, ImpactMetric> = {
  cases_per_fte: { key: 'cases_per_fte', label: 'Cases per advocate', value: 1.5, unit: '×', baseline: 1.0, direction: 'up' },
  manual: { key: 'manual', label: 'Manual work', value: 14, unit: '%', baseline: 90, direction: 'down' },
  auto_steps: { key: 'auto_steps', label: 'Steps automated today', value: 36, unit: '', direction: 'up' },
  fields: { key: 'fields', label: 'Fields auto-filled', value: 72, unit: '', direction: 'up' },
  steps: { key: 'steps', label: 'Workflow steps', value: 64, unit: '', baseline: 160, direction: 'down' },
  doc_turnaround: { key: 'doc_turnaround', label: 'Doc turnaround', value: -60, unit: '%', direction: 'down' },
  forward_revenue: { key: 'forward_revenue', label: 'Forward revenue (3 mo)', value: 480000, unit: '$', direction: 'up' },
  ar_automated: { key: 'ar_automated', label: 'AR follow-up automated', value: 82, unit: '%', direction: 'up' },
  at_risk: { key: 'at_risk', label: 'At-risk auto-flagged', value: 8, unit: '', direction: 'up' },
  revenue_protected: { key: 'revenue_protected', label: 'Revenue protected', value: 115000, unit: '$', direction: 'up' },
  state_parsed: { key: 'state_parsed', label: 'State responses auto-parsed', value: 90, unit: '%', direction: 'up' },
  cycle_time: { key: 'cycle_time', label: 'Submission cycle time', value: -38, unit: '%', direction: 'down' },
  team_throughput: { key: 'team_throughput', label: 'Team throughput', value: 2.4, unit: '×', baseline: 1.0, direction: 'up' },
  auto_assign: { key: 'auto_assign', label: 'Auto-assign coverage', value: 78, unit: '%', direction: 'up' },
  capacity: { key: 'capacity', label: 'Capacity headroom', value: 32, unit: '%', direction: 'up' },
  growth: { key: 'growth', label: 'Program growth', value: 3.0, unit: '×', baseline: 1.0, direction: 'up' },
};

interface ImpactCopy {
  headline: string;
  subhead: string;
  capabilities: string[];
  metrics: string[];
}

/** Per-role copy — mirrors services/ai_gateway/mock_service.py `_IMPACT_COPY`. */
const IMPACT_COPY: Record<RoleViewKey, ImpactCopy> = {
  pas: {
    headline: "Queue re-ranked — you're working the top 20% by conversion probability first.",
    subhead: 'The platform ranks and pre-fills your work so effort goes to the cases most likely to convert.',
    capabilities: ['Dynamic Work Queues', 'Propensity Scoring', 'Next-Best-Action', 'Auto-population'],
    metrics: ['cases_per_fte', 'manual', 'auto_steps', 'fields'],
  },
  bas: {
    headline: 'Enrollment steps collapsed from 160 to 64 — the platform runs the busywork.',
    subhead: 'Document intake, outreach cadence, and form population happen automatically so you focus on judgment calls.',
    capabilities: ['Doc automation', 'Auto-population', 'Auto-outreach cadence', 'Decision Support'],
    metrics: ['steps', 'doc_turnaround', 'cases_per_fte', 'fields'],
  },
  finance: {
    headline: "A forward-looking view of revenue — not last month's report.",
    subhead: 'Charge capture and AR follow-up are automated; projections show where revenue is heading.',
    capabilities: ['Revenue Projections', 'AR automation', 'Decision Support'],
    metrics: ['forward_revenue', 'ar_automated', 'manual', 'cases_per_fte'],
  },
  retention: {
    headline: 'At-risk patients surface before they disengage.',
    subhead: 'Inactivity detection and omni-channel follow-up protect enrolled revenue automatically.',
    capabilities: ['Inactivity alerts', 'Omni-channel Retention', 'Maya patient assistant'],
    metrics: ['at_risk', 'revenue_protected', 'manual', 'cases_per_fte'],
  },
  state_ops: {
    headline: 'Unstructured state responses are parsed for you.',
    subhead: '~90% of inbound state email is auto-classified into structured outcomes; you handle the exceptions.',
    capabilities: ['Auto-parsed state responses', 'Doc automation', 'Auto-population'],
    metrics: ['state_parsed', 'cycle_time', 'manual', 'fields'],
  },
  clinical: {
    headline: 'Decision support surfaces the clinical signals that matter.',
    subhead: 'Screening is pre-populated and complex cases are flagged, so go/no-go calls are faster.',
    capabilities: ['Decision Support', 'Auto-population', 'Propensity Scoring'],
    metrics: ['cases_per_fte', 'fields', 'manual', 'auto_steps'],
  },
  supervisor: {
    headline: 'Scale the team 2×–4× without proportional headcount.',
    subhead: 'Auto-assignment balances load and dynamic queues surface the right work to every advocate.',
    capabilities: ['Auto-assignment / capacity', 'Dynamic Work Queues', 'What-if simulation'],
    metrics: ['team_throughput', 'auto_assign', 'capacity', 'manual'],
  },
  cfo: {
    headline: '2×–4× program growth on a platform, not linear headcount.',
    subhead: 'Forward revenue projections and per-FTE leverage replace backward-looking reporting.',
    capabilities: ['Revenue Projections', 'Program-health', 'Decision Support'],
    metrics: ['growth', 'forward_revenue', 'cases_per_fte', 'manual'],
  },
  all: {
    headline: 'The platform shifts operational complexity from people to platform.',
    subhead: 'Orchestrated workflows and embedded intelligence let a lean team operate at scale.',
    capabilities: ['Dynamic Work Queues', 'Propensity Scoring', 'Decision Support', 'Revenue Projections'],
    metrics: ['cases_per_fte', 'manual', 'steps', 'auto_steps'],
  },
};

function seedImpact(role: RoleViewKey): PlatformImpact {
  const copy = IMPACT_COPY[role] ?? IMPACT_COPY.pas;
  return {
    role,
    headline: copy.headline,
    subhead: copy.subhead,
    metrics: copy.metrics.map((k) => IMPACT_METRIC_SEED[k]).filter(Boolean),
    work_mix: IMPACT_WORK_MIX,
    steps_before: 160,
    steps_after: 64,
    capabilities: copy.capabilities,
    confidence: 0.86,
  };
}

interface RawPlatformImpact {
  role?: string;
  headline?: string;
  subhead?: string;
  metrics?: Partial<ImpactMetric>[];
  work_mix?: Partial<WorkMix>;
  steps_before?: number;
  steps_after?: number;
  capabilities?: string[];
  confidence?: number;
}

function adaptImpact(role: RoleViewKey, raw: RawPlatformImpact | null | undefined): PlatformImpact {
  if (!raw || typeof raw !== 'object' || !raw.headline) return seedImpact(role);
  const seeded = seedImpact(role);
  const metrics: ImpactMetric[] =
    raw.metrics && raw.metrics.length
      ? raw.metrics.map((m, i) => ({
          key: m.key ?? `metric-${i}`,
          label: m.label ?? '—',
          value: m.value ?? 0,
          unit: m.unit ?? '',
          baseline: m.baseline ?? null,
          direction: m.direction === 'down' ? 'down' : 'up',
        }))
      : seeded.metrics;
  return {
    role: raw.role ?? role,
    headline: raw.headline,
    subhead: raw.subhead ?? seeded.subhead,
    metrics,
    work_mix: {
      automated_pct: raw.work_mix?.automated_pct ?? IMPACT_WORK_MIX.automated_pct,
      ai_assisted_pct: raw.work_mix?.ai_assisted_pct ?? IMPACT_WORK_MIX.ai_assisted_pct,
      manual_pct: raw.work_mix?.manual_pct ?? IMPACT_WORK_MIX.manual_pct,
    },
    steps_before: raw.steps_before ?? 160,
    steps_after: raw.steps_after ?? 64,
    capabilities: raw.capabilities && raw.capabilities.length ? raw.capabilities : seeded.capabilities,
    confidence: raw.confidence ?? 0.86,
  };
}

interface UsePlatformImpactResult {
  impact: PlatformImpact | null;
  available: boolean;
  isLoading: boolean;
  refresh: () => void;
}

/** Loads the role-scoped platform-impact / scale view, seeding on 404. */
export function usePlatformImpact(role: RoleViewKey | undefined): UsePlatformImpactResult {
  const key = role ?? 'pas';
  const [impact, setImpact] = useState<PlatformImpact | null>(null);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(() => {
    setIsLoading(true);
    apiClient
      .get<RawPlatformImpact>(`/ai/platform-impact?role=${encodeURIComponent(key)}`)
      .then((data) => {
        setImpact(adaptImpact(key, data));
        setAvailable(true);
      })
      .catch(() => {
        setImpact(seedImpact(key));
        setAvailable(false);
      })
      .finally(() => setIsLoading(false));
  }, [key]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { impact, available, isLoading, refresh };
}
